/**
 * The companion's knowledge prefetch (CompanionConfig.knowledgePrefetch,
 * 2026-10-18): the search a concept turn's model makes first, made for it
 * alongside the turn check so round 0 can answer: the glossary and the
 * columns. A how-to turn keeps today's path, with the model asking the
 * platform guide itself (Chris, 2026-10-19). Since 2026-10-22 a question
 * about the trader's own chart gets the chart read the same way (read_chart).
 */
import type { CompanionConfig } from '@edge/edge-ai'
import { splitMentionPreamble } from '@/components/chat/mention-preamble'
import { companionClient } from '@/services/api/api-client'
import { useActionRegistrationStore } from './action-registration-store'
import { runtimeToolExecutor } from './tool-executor'

/** The knowledge searches take at most 500 characters. */
const MAX_QUERY_CHARS = 500

/** The entries a concept turn gets; the first ones carry the answer. */
export const PREFETCH_LIMIT = 5

/** Logged once per chart read that failed; the model then calls read_chart itself. */
const CHART_READ_FAILED = '[companion] chart read before round 0 failed:'

interface KnowledgeHit {
  id?: string
  title: string
  domain: string
  score: number
  description: string
  config: unknown
}

interface KnowledgeConcept {
  results?: KnowledgeHit[]
  vocabulary?: Array<{ term: string; meaning: string }>
}

/** What the trader asked: no @-mention preamble, within 500 characters. */
function questionOf(message: string): string {
  return splitMentionPreamble(message).body.trim().slice(0, MAX_QUERY_CHARS)
}

/**
 * Hits as the search_knowledge tool shows them (tool-executor.ts
 * formatKnowledge; the unit test pins the two equal): the matched trader
 * terms, then each entry with its domain and score.
 */
function formatKnowledge(c: KnowledgeConcept): string {
  const formatted = (c.results || [])
    .map(
      (r) =>
        `**${r.title}** (${r.domain}, score: ${r.score.toFixed(2)})\n${r.description}\nConfig: ${JSON.stringify(r.config)}`,
    )
    .join('\n\n---\n\n')
  const terms = (c.vocabulary || []).map((v) => `- ${v.term}: ${v.meaning}`).join('\n')
  const header = terms ? `Trader terms matched:\n${terms}\n\n` : ''
  return header + formatted
}

export const knowledgePrefetch: NonNullable<CompanionConfig['knowledgePrefetch']> = {
  /**
   * The glossary and the columns, searched together and merged by score, each
   * entry keeping its domain: "what is float" is often best answered by the
   * Float column. Null when neither found anything or both failed: the model
   * then searches itself, as before.
   */
  async concept(message, signal) {
    const query = questionOf(message)
    if (!query || signal.aborted) return null
    const search = (domain: string) =>
      companionClient
        .post<KnowledgeConcept>(
          '/v1/companion/search-knowledge',
          { query, domain, limit: PREFETCH_LIMIT },
          { signal },
        )
        .catch(() => null)
    const found = await Promise.all([search('concept'), search('column')])
    const seen = new Set<string>()
    const results = found
      .flatMap((c) => c?.results ?? [])
      .filter((r) => {
        const key = r.id ?? `${r.domain}:${r.title}`
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, PREFETCH_LIMIT)
    const vocabulary = [
      ...new Map(found.flatMap((c) => c?.vocabulary ?? []).map((v) => [v.term, v])).values(),
    ]
    if (!results.length && !vocabulary.length) return null
    return { input: { query }, content: formatKnowledge({ results, vocabulary }) }
  },

  /**
   * read_chart on the chart on screen, with the tool's own executor and the
   * page's callbacks: what the model reads when it calls the tool itself. Null
   * when the chart tools are not registered (aichartdrawing-release is off), the
   * page registered no callbacks, or the read failed (no chart, an ambiguous
   * one): the model then calls read_chart itself, as before. Read-only.
   */
  chart(input) {
    const execute = runtimeToolExecutor('read_chart')
    const callbacks = useActionRegistrationStore.getState().callbacks
    if (!execute || !callbacks) return null
    try {
      const result = execute(
        { tool_use_id: 'chart_prefetch', name: 'read_chart', input },
        callbacks,
      )
      // The read is synchronous; the turn does not wait on one that is not.
      if (result instanceof Promise) return null
      if (!result.success) {
        console.warn(CHART_READ_FAILED, result.content)
        return null
      }
      return { input, content: result.content }
    } catch (e) {
      console.warn(CHART_READ_FAILED, e)
      return null
    }
  },
}
